import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConfig, container } from 'src/inversify.config';
import { AuditLogOptions, AuditLogSummary, AuditLogSummaryStore } from 'src/stores/audit-logs-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import * as Moment from 'moment';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import ExportButton from 'src/common/components/export-button';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import i18next from 'i18next';

const GroupLogsTab: FC<{ groupId: string }> = ({ groupId }) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'timestamp', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
    filter: [
      {
        and: [`relatedEntityId eq ${groupId}`],
      },
    ],
  } as Query);

  const logsStore = container.get(AuditLogSummaryStore);
  const logsState = logsStore.state;

  useEffect(() => {
    const newQuery = {
      searchQuery: '',
      orderBy: [{ field: 'timestamp', direction: 'Descending', useProfile: false }],
      skip: 0,
      take: 10,
      filter: [
        {
          and: [`relatedEntityId eq ${groupId}`],
        },
      ],
    } as Query;
    setQuery(newQuery);
    logsStore.load(newQuery);
  }, [groupId, logsStore]);

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        // searchable: false,
        filter: <DateRangeFilter />,
        fiterField: 'timestamp',
        field: 'timestamp',
        title: t('Timestamp'),
        renderer: (data: AuditLogSummary) => (
          <span>{Moment.utc(data.timestamp).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>
        ),
      },
      {
        sortable: true,
        filter: <OptionsFilter options={AuditLogOptions.map((v) => ({ label: t(v.label), value: v.value }))} />,
        fiterField: 'type',
        field: 'type',
        title: t('LogType'),
        renderer: (data: AuditLogSummary) => <Tag>{t(data.type)}</Tag>,
      },
      {
        searchable: true,
        field: 'description',
        title: t('Description'),
        renderer: (data: AuditLogSummary) => <span>{data.description}</span>,
      },
      {
        sortable: true,
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={logsStore.baseUrl} fieldName={'userDisplayName'} fieldValue={'userDisplayName'} />,
        fiterField: 'userId',
        field: 'userId',
        title: t('RelatedUser'),
        renderer: (data: AuditLogSummary) => <span>{data.userDisplayName}</span>,
      },
    ],
    data: { ...logsState.value, isBusy: logsState.value.isBusy },
  } as TableModel<AuditLogSummary>;

  return (
    <div>
      <TableView
        rowKey="id"
        title={t('GroupsLogs')}
        titlePadding={'0 40px'}
        canCreateNew={false}
        canDelete={false}
        canEdit={false}
        canSort
        onQueryChanged={(query: Query) => {
          setQuery(query);
          logsStore.load(query);
        }}
        onRefresh={() => logsStore.load(query)}
        model={logsState.errorMessage.value ? emptyTableModel : tableModel}
        error={
          logsState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(logsState.errorMessage.value) : t('GroupLogsTableError'))
        }
        rightToolbar={
          <li>
            <ExportButton
              itemsCount={logsState.count.get()}
              maxItemsCount={500}
              onExport={() => logsStore.exportCsv(query)}
              tooltip={t('ExportCSV')}
            />
          </li>
        }
      />
    </div>
  );
};

export default GroupLogsTab;
